import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { useApp } from '../context/AppContext';

const CATEGORIES = ['Meal', 'Beverage', 'Snack', 'Non-Veg', 'Side', 'General'];

const emptyForm = { name: '', category: 'Meal', price: '', unit: 'plate', is_active: true };

export default function Items() {
  const { toast } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'add' | 'edit'
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('All');

  const load = () => {
    api.getItems().then(d => { setItems(d); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(emptyForm); setEditId(null); setModal('form'); };
  const openEdit = (item) => {
    setForm({ name: item.name, category: item.category, price: item.price, unit: item.unit, is_active: !!item.is_active });
    setEditId(item.id);
    setModal('form');
  };

  const handleSave = async () => {
    if (!form.name || !form.price) return toast('Name and price are required', 'error');
    try {
      if (editId) {
        await api.updateItem(editId, { ...form, price: parseFloat(form.price) });
        toast('Item updated');
      } else {
        await api.createItem({ ...form, price: parseFloat(form.price) });
        toast('Item added');
      }
      setModal(null);
      load();
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const handleToggle = async (item) => {
    await api.updateItem(item.id, { ...item, is_active: item.is_active ? 0 : 1 });
    toast(item.is_active ? 'Item deactivated' : 'Item activated');
    load();
  };

  const cats = ['All', ...CATEGORIES];
  const filtered = items.filter(i => {
    const matchCat = catFilter === 'All' || i.category === catFilter;
    const matchSearch = i.name.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div className="page-body">
      <div className="search-bar">
        <div className="search-input-wrap" style={{ flex: 1 }}>
          <span className="search-icon">🔍</span>
          <input placeholder="Search items..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Item</button>
      </div>

      <div className="cat-tabs">
        {cats.map(c => (
          <button key={c} className={`cat-tab ${catFilter === c ? 'active' : ''}`} onClick={() => setCatFilter(c)}>{c}</button>
        ))}
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Item Name</th>
                <th>Category</th>
                <th>Price</th>
                <th>Unit</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6}><div className="loading-center"><span className="spinner" /></div></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6}><div className="empty-state"><p>No items found</p></div></td></tr>
              ) : filtered.map(item => (
                <tr key={item.id}>
                  <td style={{ color: 'var(--iocl-white)', fontWeight: 500 }}>{item.name}</td>
                  <td><span className="badge badge-blue">{item.category}</span></td>
                  <td className="text-orange" style={{ fontWeight: 600 }}>₹{item.price}</td>
                  <td style={{ color: 'var(--iocl-muted)' }}>{item.unit}</td>
                  <td>
                    <span className={`badge ${item.is_active ? 'badge-green' : 'badge-red'}`}>
                      {item.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(item)}>Edit</button>
                      <button
                        className={`btn btn-sm ${item.is_active ? 'btn-danger' : 'btn-success'}`}
                        onClick={() => handleToggle(item)}
                      >
                        {item.is_active ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal === 'form' && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setModal(null)}>
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">{editId ? 'Edit Item' : 'Add New Item'}</h3>
              <button className="btn btn-secondary btn-sm btn-icon" onClick={() => setModal(null)}>✕</button>
            </div>

            <div className="form-group">
              <label>Item Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Thali" />
            </div>
            <div className="two-col">
              <div className="form-group">
                <label>Category *</label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Unit</label>
                <select value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}>
                  {['plate', 'cup', 'glass', 'bowl', 'piece', 'kg', 'litre'].map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Price (₹) *</label>
              <input type="number" min="0" step="0.5" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="0.00" />
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}>
                {editId ? 'Save Changes' : 'Add Item'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
